import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { supabase } from '../../supabaseClient';

export const isDocumentShared = (item, now = Date.now()) => Boolean(item?.isShared)
  && (!item.share_expires_at || new Date(item.share_expires_at).getTime() > now);

export function useDocumentSharing({ historyItems, setHistoryItems, setAlert, scopeKey }) {
  const [pending, setPending] = useState(null);
  const [expires, setExpires] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setPending(null);
    setSaving(false);
    setError('');
    return () => { generation.current += 1; };
  }, [scopeKey]);
  const [clock, setClock] = useState(0);
  useEffect(() => {
    const times = historyItems.filter(item => item.isShared && item.share_expires_at)
      .map(item => new Date(item.share_expires_at).getTime()).filter(time => time > Date.now());
    if (!times.length) return undefined;
    const timer = setTimeout(() => setClock(value => value + 1), Math.min(Math.min(...times) - Date.now() + 1, 2147483647));
    return () => clearTimeout(timer);
  }, [historyItems, clock]);
  const persist = async (item, shared, expiry) => {
    if (busy.current) return false;
    const version = generation.current;
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      const report = item.type === 'Document';
      const { data, error: saveError } = await supabase.from(report ? 'generatedreports' : 'consultations')
        .update({ isShared: shared, share_expires_at: expiry, [report ? 'updated_at' : 'updatedAt']: new Date().toISOString() })
        .eq('id', item.id).select('id,isShared,share_expires_at').single();
      if (saveError || !data) throw new Error('Share not saved');
      if (version !== generation.current) return false;
      setHistoryItems(previous => previous.map(row => row.id === item.id ? { ...row, isShared: data.isShared, share_expires_at: data.share_expires_at } : row));
      setPending(null);
      setAlert(shared ? 'Shared with the client on MindVault.' : 'Sharing stopped. The client can no longer open this document in MindVault.', 'success');
      return true;
    } catch {
      if (version !== generation.current) return false;
      const message = 'Sharing change not saved. Please retry.';
      setError(message);
      if (!pending) setAlert(message, 'error');
      return false;
    } finally { if (version === generation.current) { busy.current = false; setSaving(false); } }
  };
  const handleItemShare = async item => {
    if (!['Document', 'Consultation'].includes(item?.type) || busy.current) return false;
    if (isDocumentShared(item)) return persist(item, false, null);
    setPending(item);
    setExpires('');
    setError('');
    return false;
  };
  const confirm = () => {
    const date = expires ? new Date(expires) : null;
    if (date && (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now())) {
      setError('Choose a valid expiry date and time in the future.');
      return;
    }
    persist(pending, true, date?.toISOString() || null);
  };
  const sharingDialog = <Dialog open={Boolean(pending)} onClose={saving ? undefined : () => setPending(null)} aria-labelledby="document-sharing-title" fullWidth maxWidth="sm">
    <DialogTitle id="document-sharing-title">Share document with client</DialogTitle>
    <DialogContent>
      <Typography sx={{ mb: 2 }}>The client can read this report or transcript while your connection and their sharing consent remain active. An optional expiry stops future access automatically.</Typography>
      <TextField fullWidth type="datetime-local" label="Access expires at (optional)" value={expires} onChange={event => setExpires(event.target.value)} disabled={saving} InputLabelProps={{ shrink: true }} helperText={`Leave blank for no expiry. Time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}.`} />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </DialogContent>
    <DialogActions><Button disabled={saving} onClick={() => setPending(null)}>Cancel</Button><Button variant="contained" disabled={saving} onClick={confirm}>Share document</Button></DialogActions>
  </Dialog>;
  return { handleItemShare, sharingDialog };
}
